'use client';

import React, { FormEvent, useState } from 'react';
import {
  ArrowLeft,
  CheckCircle2,
  Eye,
  EyeOff,
  FileText,
  Lock,
  Mail,
  Upload,
  UserRound,
} from 'lucide-react';

interface EvaluatorRegistrationFormProps {
  onBack: () => void;
}

const inputClass =
  'w-full rounded-xl border border-border bg-background px-4 py-3 text-sm text-foreground outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/10';

const labelClass =
  'mb-1.5 block text-sm font-700 text-foreground';

const ESTADOS_MEXICO = [
  'Aguascalientes',
  'Baja California',
  'Baja California Sur',
  'Campeche',
  'Chiapas',
  'Chihuahua',
  'Ciudad de México',
  'Coahuila',
  'Colima',
  'Durango',
  'Estado de México',
  'Guanajuato',
  'Guerrero',
  'Hidalgo',
  'Jalisco',
  'Michoacán',
  'Morelos',
  'Nayarit',
  'Nuevo León',
  'Oaxaca',
  'Puebla',
  'Querétaro',
  'Quintana Roo',
  'San Luis Potosí',
  'Sinaloa',
  'Sonora',
  'Tabasco',
  'Tamaulipas',
  'Tlaxcala',
  'Veracruz',
  'Yucatán',
  'Zacatecas',
];


export default function EvaluatorRegistrationForm({
  onBack,
}: EvaluatorRegistrationFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    nombreCompleto: '',
    email: '',
    password: '',
    confirmPassword: '',
    telefono: '',
    cargo: '',
    institucion: '',
    estado: '',
    municipio: '',
    profesion: '',
    areaExperiencia: '',
    aniosExperiencia: '',
    experienciaProfesional: '',
    experienciaEvaluacion: '',
    sectoresExperiencia: '',
  });

  const [cv, setCv] = useState<File | null>(null);

  function update(
    field: keyof typeof form,
    value: string
  ) {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    setError('');
    setSuccess(false);

    if (form.password !== form.confirmPassword) {
      setError('Las contraseñas no coinciden.');
      return;
    }

    if (form.password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }

    if (!cv) {
      setError('Debes adjuntar tu CV.');
      return;
    }

    setLoading(true);

    try {
      const data = new FormData();

      Object.entries(form).forEach(([key, value]) => {
        if (key !== 'confirmPassword') {
          data.append(key, value);
        }
      });

      data.append('cv', cv);

      const response = await fetch(
        '/api/registration/evaluator',
        {
          method: 'POST',
          body: data,
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(
          result?.error ||
            'No fue posible enviar la solicitud.'
        );
      }

      setSuccess(true);
    } catch (err: any) {
      setError(
        err?.message ||
          'No fue posible enviar la solicitud.'
      );
    } finally {
      setLoading(false);
    }
  }

  if (success) {
    return (
      <div className="min-h-screen bg-background px-6 py-10">
        <div className="mx-auto flex min-h-[80vh] max-w-2xl items-center justify-center">
          <div className="w-full rounded-2xl border border-border bg-card p-8 text-center shadow-sm">
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 text-green-700">
              <CheckCircle2 size={34} />
            </div>

            <h1 className="text-2xl font-800 text-foreground">
              Solicitud enviada
            </h1>

            <p className="mt-4 text-muted-foreground">
              Recibimos tu solicitud para participar como
              evaluador del Premio Nacional de Exportación.
            </p>

            <p className="mt-3 text-sm text-muted-foreground">
              Tu solicitud quedó pendiente de revisión por el
              Administrador General.
            </p>

            <p className="mt-3 text-sm text-muted-foreground">
              La aprobación de la solicitud no implica una
              asignación automática de rol.
            </p>

            <button
              type="button"
              onClick={onBack}
              className="mt-8 rounded-xl bg-primary px-6 py-3 font-700 text-primary-foreground transition hover:opacity-90"
            >
              Regresar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background px-6 py-8">
      <div className="mx-auto max-w-5xl">

        <button
          type="button"
          onClick={onBack}
          className="mb-6 inline-flex items-center gap-2 text-sm font-700 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft size={17} />
          Regresar
        </button>

        <div className="mb-8">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <UserRound size={24} />
            </div>

            <div>
              <h1 className="text-2xl font-800 text-foreground">
                Registro de evaluador
              </h1>

              <p className="text-sm text-muted-foreground">
                Solicita tu incorporación al proceso de evaluación.
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 rounded-xl border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className="space-y-6"
        >

          {/* ACCESO */}
          <section className="rounded-2xl border border-border bg-card p-6">
            <div className="mb-5">
              <h2 className="text-lg font-800">
                Datos de acceso
              </h2>

              <p className="mt-1 text-sm text-muted-foreground">
                Estos datos se utilizarán para acceder posteriormente al sistema.
              </p>
            </div>

            <div className="grid gap-5 md:grid-cols-2">

              <div className="md:col-span-2">
                <label className={labelClass}>
                  Correo electrónico *
                </label>

                <div className="relative">
                  <Mail
                    size={18}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground"
                  />

                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) =>
                      update('email', e.target.value)
                    }
                    className={`${inputClass} pl-11`}
                    placeholder="correo@ejemplo.com"
                  />
                </div>
              </div>

              <div>
                <label className={labelClass}>
                  Contraseña *
                </label>

                <div className="relative">
                  <Lock
                    size={18}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground"
                  />

                  <input
                    type={
                      showPassword
                        ? 'text'
                        : 'password'
                    }
                    required
                    minLength={8}
                    value={form.password}
                    onChange={(e) =>
                      update('password', e.target.value)
                    }
                    className={`${inputClass} pl-11 pr-11`}
                    placeholder="Mínimo 8 caracteres"
                  />

                  <button
                    type="button"
                    onClick={() =>
                      setShowPassword((value) => !value)
                    }
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground"
                  >
                    {showPassword ? (
                      <EyeOff size={18} />
                    ) : (
                      <Eye size={18} />
                    )}
                  </button>
                </div>
              </div>

              <div>
                <label className={labelClass}>
                  Confirmar contraseña *
                </label>

                <div className="relative">
                  <Lock
                    size={18}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground"
                  />

                  <input
                    type={
                      showConfirmPassword
                        ? 'text'
                        : 'password'
                    }
                    required
                    value={form.confirmPassword}
                    onChange={(e) =>
                      update(
                        'confirmPassword',
                        e.target.value
                      )
                    }
                    className={`${inputClass} pl-11 pr-11`}
                    placeholder="Repite tu contraseña"
                  />

                  <button
                    type="button"
                    onClick={() =>
                      setShowConfirmPassword(
                        (value) => !value
                      )
                    }
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground"
                  >
                    {showConfirmPassword ? (
                      <EyeOff size={18} />
                    ) : (
                      <Eye size={18} />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* DATOS PERSONALES */}
          <section className="rounded-2xl border border-border bg-card p-6">
            <h2 className="mb-5 text-lg font-800">
              Datos personales y laborales
            </h2>

            <div className="grid gap-5 md:grid-cols-2">

              <div className="md:col-span-2">
                <label className={labelClass}>
                  Nombre completo *
                </label>

                <input
                  required
                  value={form.nombreCompleto}
                  onChange={(e) =>
                    update(
                      'nombreCompleto',
                      e.target.value
                    )
                  }
                  className={inputClass}
                  placeholder="Nombre y apellidos"
                />
              </div>

              <div>
                <label className={labelClass}>
                  Teléfono *
                </label>

                <input
                  required
                  value={form.telefono}
                  onChange={(e) =>
                    update('telefono', e.target.value)
                  }
                  className={inputClass}
                  placeholder="10 dígitos"
                />
              </div>

              <div>
                <label className={labelClass}>
                  Cargo *
                </label>

                <input
                  required
                  value={form.cargo}
                  onChange={(e) =>
                    update('cargo', e.target.value)
                  }
                  className={inputClass}
                  placeholder="Cargo actual"
                />
              </div>

              <div>
                <label className={labelClass}>
                  Institución / empresa
                </label>

                <input
                  value={form.institucion}
                  onChange={(e) =>
                    update('institucion', e.target.value)
                  }
                  className={inputClass}
                  placeholder="Nombre de la institución"
                />
              </div>

              <div>
                <label className={labelClass}>
                  Estado
                </label>

                <select
                  value={form.estado}
                  onChange={(e) =>
                    update('estado', e.target.value)
                  }
                  className={inputClass}
                >
                  <option value="">
                    Seleccionar estado…
                  </option>

                  {ESTADOS_MEXICO.map((estado) => (
                    <option
                      key={estado}
                      value={estado}
                    >
                      {estado}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>
                  Municipio / alcaldía
                </label>

                <input
                  value={form.municipio}
                  onChange={(e) =>
                    update('municipio', e.target.value)
                  }
                  className={inputClass}
                  placeholder="Municipio o alcaldía"
                />
              </div>
            </div>
          </section>

          {/* PERFIL */}
          <section className="rounded-2xl border border-border bg-card p-6">
            <h2 className="mb-5 text-lg font-800">
              Perfil profesional
            </h2>

            <div className="grid gap-5 md:grid-cols-2">

              <div>
                <label className={labelClass}>
                  Profesión / especialidad *
                </label>

                <input
                  required
                  value={form.profesion}
                  onChange={(e) =>
                    update('profesion', e.target.value)
                  }
                  className={inputClass}
                  placeholder="Ej. Comercio exterior"
                />
              </div>

              <div>
                <label className={labelClass}>
                  Área de experiencia
                </label>

                <input
                  value={form.areaExperiencia}
                  onChange={(e) =>
                    update(
                      'areaExperiencia',
                      e.target.value
                    )
                  }
                  className={inputClass}
                  placeholder="Área principal"
                />
              </div>

              <div>
                <label className={labelClass}>
                  Años de experiencia
                </label>

                <input
                  type="number"
                  min="0"
                  value={form.aniosExperiencia}
                  onChange={(e) =>
                    update(
                      'aniosExperiencia',
                      e.target.value
                    )
                  }
                  className={inputClass}
                  placeholder="Ej. 10"
                />
              </div>

              <div>
                <label className={labelClass}>
                  Sectores de experiencia
                </label>

                <input
                  value={form.sectoresExperiencia}
                  onChange={(e) =>
                    update(
                      'sectoresExperiencia',
                      e.target.value
                    )
                  }
                  className={inputClass}
                  placeholder="Ej. Automotriz, alimentos..."
                />
              </div>

              <div className="md:col-span-2">
                <label className={labelClass}>
                  Experiencia profesional
                </label>

                <textarea
                  rows={4}
                  value={form.experienciaProfesional}
                  onChange={(e) =>
                    update(
                      'experienciaProfesional',
                      e.target.value
                    )
                  }
                  className={inputClass}
                  placeholder="Describe brevemente tu experiencia profesional."
                />
              </div>

              <div className="md:col-span-2">
                <label className={labelClass}>
                  Experiencia como evaluador
                </label>

                <textarea
                  rows={4}
                  value={form.experienciaEvaluacion}
                  onChange={(e) =>
                    update(
                      'experienciaEvaluacion',
                      e.target.value
                    )
                  }
                  className={inputClass}
                  placeholder="Describe tu experiencia previa como evaluador, jurado o actividades similares."
                />
              </div>
            </div>
          </section>

          {/* DOCUMENTOS */}
          <section className="rounded-2xl border border-border bg-card p-6">
            <div className="mb-5">
              <h2 className="text-lg font-800">
                Documentación
              </h2>

              <p className="mt-1 text-sm text-muted-foreground">
                Adjunta los documentos requeridos en PDF, DOC o DOCX. Máximo 10 MB por archivo.
              </p>
            </div>

            <div className="grid gap-4">

              <FileField
                label="CV *"
                description="Currículum vitae actualizado. PDF, DOC o DOCX. Máximo 10 MB."
                file={cv}
                onChange={setCv}
              />

            </div>
          </section>

          <div className="rounded-xl border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
            <strong className="text-foreground">
              Importante:
            </strong>{' '}
            esta solicitud será revisada por el Administrador
            General. El registro no asigna automáticamente
            ningún rol dentro del sistema.
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-xl bg-primary py-3.5 font-800 text-primary-foreground transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading
              ? 'Enviando solicitud...'
              : 'Enviar solicitud de registro'}
          </button>

        </form>
      </div>
    </div>
  );
}

interface FileFieldProps {
  label: string;
  description: string;
  file: File | null;
  onChange: (file: File | null) => void;
}

function FileField({
  label,
  description,
  file,
  onChange,
}: FileFieldProps) {
  return (
    <div className="rounded-xl border border-dashed border-border bg-background p-5 transition hover:border-primary/50 hover:bg-muted/30">
      <div className="flex items-start gap-4">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          {file ? (
            <CheckCircle2 size={21} />
          ) : (
            <FileText size={21} />
          )}
        </div>

        <div className="min-w-0 flex-1">
          <p className="font-700 text-foreground">
            {label}
          </p>

          <p className="mt-1 text-xs text-muted-foreground">
            {description}
          </p>

          <p className="mt-2 truncate text-sm font-600 text-primary">
            {file
              ? file.name
              : 'Ningún archivo seleccionado'}
          </p>

          {file && (
            <div className="mt-3 flex items-center gap-3">
              <label className="cursor-pointer text-xs font-700 text-primary hover:underline">
                Cambiar archivo
                <input
                  type="file"
                  className="hidden"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={(event) => {
                    onChange(event.target.files?.[0] || null);
                    event.currentTarget.value = '';
                  }}
                />
              </label>

              <button
                type="button"
                onClick={() => onChange(null)}
                className="text-xs font-700 text-destructive hover:underline"
              >
                Quitar archivo
              </button>
            </div>
          )}

          {!file && (
            <label className="mt-3 inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border px-3 py-2 text-xs font-700 text-foreground hover:bg-muted">
              <Upload size={15} />
              Seleccionar archivo
              <input
                type="file"
                className="hidden"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={(event) => {
                  onChange(event.target.files?.[0] || null);
                  event.currentTarget.value = '';
                }}
              />
            </label>
          )}
        </div>
      </div>
    </div>
  );
}
